import {
  getAllowedInstallments,
  IBudget,
  TBudgetSigningMode,
  TBudgetStatus,
  TSaleType,
} from 'modules/checkout/domain/entities';
import { TChannelType } from 'shared/domain/entities';
import {
  Customer,
  IVectisCustomer,
  PAYMENT_METHOD_TO_DOMAIN,
  TVectisPaymentMethodType,
} from 'shared/infra/adapters';

import {
  buildMissingCustomerFields,
  IVectisMissingCustomerField,
} from './MissingCustomerFields';
import {
  buildPaymentMethods as buildPaymentMethodsFromVectis,
  IVectisPaymentMethod,
} from './PaymentMethod';

export type TVectisStatus =
  | 'ABERTO'
  | 'AGUARDANDO_APROVACAO'
  | 'AGUARDANDO_ASSINATURA'
  | 'AGUARDANDO_PAGAMENTO'
  | 'AGUARDANDO_PROCESSAMENTO'
  | 'AGUARDANDO_REABERTURA'
  | 'APROVACAO_PAGAMENTO'
  | 'CONTRATO_GERADO'
  | 'CONTRATO_FECHADO'
  | 'EM_PROCESSO_PAGAMENTO'
  | 'FECHADO'
  | 'ORCAMENTO_FECHADO'
  | 'INICIADO'
  | 'PERDIDO';

export interface IVectisBudget {
  idOrcamento: number;
  hashOrcamento: string;
  idUnidade: number;
  cliente: IVectisCustomer;
  statusOrcamento: TVectisStatus;
  tipoVenda?: 'PRODUTO_AVULSO' | 'PACOTE_COMBO';
  itens: {
    id: number;
    titulo: string;
    isCombo: boolean;
    quantidadeSessoes: number;
    urlImagem: string;
    urlImagemAltaResolucaoEcommerce: string;
    urlImagemBaixaResolucaoEcommerce: string;
    urlImagemMasculinaAltaResolucao: string;
    urlImagemMasculinaBaixaResolucao: string;
    urlImagemFemininaAltaResolucao: string;
    urlImagemFemininaBaixaResolucao: string;
    classificacao: string[];
    avisos: string[];
    msgBloqueioVenda?: string;
    preco: {
      precoDe: number;
      precoPor: number;
      maximoParcelasRecorrente: number;
      valorParcelaRecorrente: number;
    };
    brindes?: any;
    tipo?: any;
    precos: {
      [key: string]: IVectisPaymentMethod;
    }[];
  }[];
  formasPagamento: IVectisPaymentMethod[];
  configuracoes: {
    parcelaReduzida: {
      habilitado: boolean;
      valorMinimoOrcamento: number;
      valorParcela: number;
      numeroMaximoParcelasFixas: number;
    };
    adiamentoProximaParcela: {
      condicao: string;
      habilitado: boolean;
      qtdMesesAdiamentoProximaParcela: number;
    };
    fomentoFormasPagamento?: {
      habilitado: boolean;
      ofertas: Partial<
        Record<TVectisPaymentMethodType, TVectisPaymentMethodType[]>
      >;
    };
  };
  cupom?: {
    id: number;
    codigo: string;
    percentualDesconto?: number;
    valorDesconto?: number;
  };
  dataHoraExpiracao?: string;
  formaDePagamentoEscolhida?: TVectisPaymentMethodType;
  parcelasEscolhidas?: number;
  dadosPagamento?: {
    promocaoParcelaReduzidaValorFixo: boolean;
    formaPagamento: TVectisPaymentMethodType;
    numeroParcelas: number;
    condicaoPagtoBoletoFlex: any;
    cobrancaCheckoutIsenta: boolean;
    condicaoPagamento?: unknown[];
  };
  canaisEnvioCodigoConfirmacao: TChannelType[];
  foneCelularMascarado?: string;
  emailMascarado?: string;
  valorMaximoParcelaPermitidoContratoRecorrente: number;
  tipoAssinaturaContrato: TBudgetSigningMode;
  camposFaltantesCliente?: IVectisMissingCustomerField[];
}

export const STATUS_DICTIONARY: Record<TVectisStatus, TBudgetStatus> = {
  ABERTO: 'OPENED',
  AGUARDANDO_APROVACAO: 'WAITING_APPROVAL',
  AGUARDANDO_ASSINATURA: 'WAITING_SIGNATURE',
  AGUARDANDO_PAGAMENTO: 'WAITING_PAYMENT',
  AGUARDANDO_PROCESSAMENTO: 'WAITING_PROCESSING',
  AGUARDANDO_REABERTURA: 'WAITING_OPENING',
  APROVACAO_PAGAMENTO: 'PAYMENT_APPROVAL',
  CONTRATO_GERADO: 'CONTRACTED',
  CONTRATO_FECHADO: 'CLOSED',
  EM_PROCESSO_PAGAMENTO: 'PAYMENT_PROCESSING',
  FECHADO: 'CLOSED',
  ORCAMENTO_FECHADO: 'BUDGET_CLOSED',
  INICIADO: 'INITIATED',
  PERDIDO: 'LOST',
};

export const SALE_TYPE_ADAPTER: Record<
  NonNullable<IVectisBudget['tipoVenda']>,
  TSaleType
> = {
  PRODUTO_AVULSO: 'PRODUCT',
  PACOTE_COMBO: 'PACKAGE_COMBO',
};

export class Budget implements IBudget {
  public id: IBudget['id'];
  public hashId: IBudget['hashId'];
  public unitId: IBudget['unitId'];
  public saleType: IBudget['saleType'];
  public selectedInstallments: IBudget['selectedInstallments'];
  public selectedPaymentType: IBudget['selectedPaymentType'];
  public status: IBudget['status'];
  public signingMode: IBudget['signingMode'];
  public simultaneousPaymentMethods: IBudget['simultaneousPaymentMethods'];
  public items: IBudget['items'];
  public paymentMethods: IBudget['paymentMethods'];
  public disabledPaymentMethods: IBudget['disabledPaymentMethods'] = [];
  public customer: IBudget['customer'];
  public coupon: IBudget['coupon'];
  public enabledConfirmationChannels: IBudget['enabledConfirmationChannels'];
  public maskedContact: IBudget['maskedContact'];
  public expiryDate: IBudget['expiryDate'];
  public paymentConditionMaxValue: IBudget['paymentConditionMaxValue'];
  public paymentMethodOffers: IBudget['paymentMethodOffers'];
  public missingCustomerFields: IBudget['missingCustomerFields'];

  constructor(vectisBudget: IVectisBudget) {
    this.id = vectisBudget.idOrcamento;
    this.hashId = vectisBudget.hashOrcamento;
    this.unitId = vectisBudget.idUnidade;

    this.selectedInstallments = vectisBudget.parcelasEscolhidas;
    this.selectedPaymentType = vectisBudget.formaDePagamentoEscolhida
      ? PAYMENT_METHOD_TO_DOMAIN[vectisBudget.formaDePagamentoEscolhida]
      : undefined;

    this.status = STATUS_DICTIONARY[vectisBudget.statusOrcamento];
    this.signingMode = vectisBudget.tipoAssinaturaContrato;

    // Carrinhos do e-commerce/App só vendem pacotes/combos — usamos o
    // tipoVenda real quando a API o retorna, e caímos em PACKAGE_COMBO como
    // padrão seguro quando o campo ainda não vem preenchido (ED-6923).
    this.saleType = vectisBudget.tipoVenda
      ? SALE_TYPE_ADAPTER[vectisBudget.tipoVenda]
      : 'PACKAGE_COMBO';

    this.paymentMethods = this.buildPaymentMethods(
      vectisBudget.formasPagamento,
    );

    this.customer = new Customer(vectisBudget.cliente);
    this.items = this.buildItems(vectisBudget.itens);

    this.paymentConditionMaxValue =
      vectisBudget.valorMaximoParcelaPermitidoContratoRecorrente;
    this.expiryDate = vectisBudget.dataHoraExpiracao;
    this.enabledConfirmationChannels =
      vectisBudget.canaisEnvioCodigoConfirmacao;
    this.maskedContact = {
      phone: vectisBudget.foneCelularMascarado,
      email: vectisBudget.emailMascarado,
    };

    this.missingCustomerFields = buildMissingCustomerFields(
      vectisBudget.camposFaltantesCliente,
    );

    this.simultaneousPaymentMethods =
      vectisBudget.dadosPagamento?.condicaoPagamento?.length || 1;

    this.paymentMethodOffers = this.buildPaymentMethodOffers(
      vectisBudget.configuracoes?.fomentoFormasPagamento,
    );

    this.disabledPaymentMethods = this.paymentMethods
      .filter(paymentMethod => paymentMethod.eligibility?.eligible === false)
      .map(paymentMethod => paymentMethod.key);

    const recurringPaymentMethod = this.paymentMethods.find(
      paymentMethod => paymentMethod.key === 'RECURRING_CARD',
    );

    if (
      recurringPaymentMethod &&
      !this.disabledPaymentMethods.includes('RECURRING_CARD') &&
      getAllowedInstallments(recurringPaymentMethod).length === 0 &&
      recurringPaymentMethod.installments.length > 0
    ) {
      this.disabledPaymentMethods.push('RECURRING_CARD');
    }

    if (vectisBudget.cupom) {
      this.coupon = {
        id: vectisBudget.cupom.id,
        code: vectisBudget.cupom.codigo,
        discount: {
          value: vectisBudget.cupom.valorDesconto,
          percentace: vectisBudget.cupom.percentualDesconto,
        },
      };
    }
  }

  private buildPaymentMethodOffers(
    fomento: NonNullable<
      IVectisBudget['configuracoes']
    >['fomentoFormasPagamento'],
  ): IBudget['paymentMethodOffers'] {
    if (!fomento?.habilitado) {
      return { enabled: false, offers: {} };
    }

    const offers: IBudget['paymentMethodOffers']['offers'] = {};

    Object.entries(fomento.ofertas ?? {}).forEach(
      ([vectisKey, vectisOffers]) => {
        const key =
          PAYMENT_METHOD_TO_DOMAIN[vectisKey as TVectisPaymentMethodType];

        if (!key) return;

        offers[key] = (vectisOffers ?? [])
          .map(offer => PAYMENT_METHOD_TO_DOMAIN[offer])
          .filter(Boolean);
      },
    );

    return { enabled: true, offers };
  }

  private buildItems(vectisItems: IVectisBudget['itens']): IBudget['items'] {
    return vectisItems.map(item => ({
      id: item.id,
      title: item.titulo,
      type: item.isCombo ? 'COMBO' : 'PACKAGE',
      sessions: item.quantidadeSessoes,
      image:
        this.customer.gender === 'MALE'
          ? item.urlImagemMasculinaBaixaResolucao
          : item.urlImagemFemininaBaixaResolucao,
      classification: item.classificacao,
      values: {
        from: item.preco.precoDe,
        to: item.preco.precoPor,
        recurrent: {
          maxInstallments: item.preco.maximoParcelasRecorrente,
          installmentValue: item.preco.valorParcelaRecorrente,
        },
      },
      notices: item.avisos,
      blockingMessage: item.msgBloqueioVenda,
    }));
  }

  private buildPaymentMethods(
    vectisMethods: IVectisPaymentMethod[],
  ): IBudget['paymentMethods'] {
    return buildPaymentMethodsFromVectis(vectisMethods, this.signingMode);
  }
}
